import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Search, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["new", "contacted", "in_progress", "archived"];
const STATUS_STYLES = {
  new: "border-accent/50 text-accent",
  contacted: "border-sky-400/50 text-sky-300",
  in_progress: "border-amber-400/50 text-amber-300",
  archived: "border-white/20 text-dim",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContacts = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.q = search;
      const { data } = await api.get("/contacts", { params });
      setContacts(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    }
  }, [statusFilter, search, navigate]);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        setLoading(false);
      })
      .catch(() => navigate("/admin"));
  }, [navigate]);

  useEffect(() => {
    if (user) fetchContacts();
  }, [user, fetchContacts]);

  const updateStatus = async (id, status) => {
    await api.patch(`/contacts/${id}`, { status });
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const deleteContact = async (id) => {
    await api.delete(`/contacts/${id}`);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-dim">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="admin-dashboard">
      <div className="noise-overlay" />
      <header className="border-b border-white/10 bg-card/60 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-4 sm:px-8">
          <p className="font-display text-lg font-extrabold uppercase">Noste<span className="text-accent">.</span> <span className="ml-2 font-mono text-xs font-normal tracking-[0.25em] text-dim">ADMIN</span></p>
          <div className="flex items-center gap-6">
            <span className="hidden font-mono text-xs text-dim sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-mist transition-colors hover:border-accent hover:text-accent"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-10 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dim" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company…"
              className="w-full rounded-full border border-white/10 bg-card py-3 pl-11 pr-4 text-sm text-ink outline-none transition-colors focus:border-accent"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter("")}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${!statusFilter ? "border-accent bg-accent text-canvas" : "border-white/15 text-mist hover:text-ink"}`}
              data-testid="admin-filter-all"
            >
              All
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${statusFilter === s ? "border-accent bg-accent text-canvas" : "border-white/15 text-mist hover:text-ink"}`}
                data-testid={`admin-filter-${s}`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10 bg-card" data-testid="admin-enquiries-table">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Company</th>
                <th className="px-6 py-4">Budget</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                  data-testid={`admin-enquiry-row-${c.id}`}
                >
                  <td className="px-6 py-4 font-mono text-xs text-dim">{new Date(c.created_at).toLocaleDateString("fi-FI")}</td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-ink">{c.name}</p>
                    <p className="text-xs text-dim">{c.email}</p>
                  </td>
                  <td className="px-6 py-4 text-mist">{c.company || "—"}</td>
                  <td className="px-6 py-4 font-mono text-xs text-mist">{c.budget || "—"}</td>
                  <td className="px-6 py-4">
                    <select
                      value={c.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateStatus(c.id, e.target.value)}
                      className={`rounded-full border bg-canvas px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest outline-none ${STATUS_STYLES[c.status]}`}
                      data-testid={`admin-enquiry-status-${c.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteContact(c.id);
                      }}
                      className="text-dim transition-colors hover:text-red-400"
                      data-testid={`admin-enquiry-delete-${c.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-dim" data-testid="admin-empty-state">
                    No enquiries yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80 p-4 backdrop-blur-md"
            onClick={() => setSelected(null)}
            data-testid="admin-enquiry-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-xl rounded-2xl border border-white/10 bg-card p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink">{selected.name}</h3>
                  <p className="mt-1 font-mono text-xs text-accent">{selected.email}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-dim transition-colors hover:text-ink" data-testid="admin-enquiry-modal-close" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 font-mono text-xs">
                <div>
                  <p className="uppercase tracking-[0.2em] text-dim">Company</p>
                  <p className="mt-1 text-ink">{selected.company || "—"}</p>
                </div>
                <div>
                  <p className="uppercase tracking-[0.2em] text-dim">Budget</p>
                  <p className="mt-1 text-ink">{selected.budget || "—"}</p>
                </div>
                <div>
                  <p className="uppercase tracking-[0.2em] text-dim">Services</p>
                  <p className="mt-1 text-ink">{selected.services?.length ? selected.services.join(", ") : "—"}</p>
                </div>
                <div>
                  <p className="uppercase tracking-[0.2em] text-dim">Received</p>
                  <p className="mt-1 text-ink">{new Date(selected.created_at).toLocaleString("fi-FI")}</p>
                </div>
              </div>
              <div className="mt-6">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-dim">Message</p>
                <p className="mt-2 whitespace-pre-wrap rounded-lg border border-white/10 bg-canvas p-4 text-sm leading-relaxed text-mist">{selected.message}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
